/**
 * ROUTE-02 / ROUTE-04a — the proximity route engine. Pure functions: haversine
 * distance, route length, nearest-neighbor + 2-opt, and a time-band-constrained
 * optimizer that honors windows + a start position. Used by the Board map's
 * "Optimize" button and (later) the SEG-01 campaign batcher.
 *
 * Open route (no return to depot). Straight-line miles × a road fudge is a fine
 * proxy for ordering; real drive distances/times come later (Distance Matrix).
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Service-region sanity bounds — all of MN plus the WI/IA/Dakota borders. A
 * geocode outside this box is a wrong-place match (real case: "Bloomington, MN"
 * geocoded to Seattle), and one such point silently poisons every mileage and
 * drive-time number on the board. Treat out-of-region exactly like un-geocoded:
 * the stop stays on the schedule but never enters route math or the map.
 * (AUDIT-2026-06-09 A1; bad rows also quarantined in pegasus_geocode_quarantine.)
 */
export const SERVICE_BOUNDS = {
  latMin: 42.5,
  latMax: 49.5,
  lngMin: -97.5,
  lngMax: -89.5,
} as const;

export function inServiceArea(lat: number, lng: number): boolean {
  return (
    lat >= SERVICE_BOUNDS.latMin &&
    lat <= SERVICE_BOUNDS.latMax &&
    lng >= SERVICE_BOUNDS.lngMin &&
    lng <= SERVICE_BOUNDS.lngMax
  );
}

/**
 * The one predicate for "these coords may enter route math / the map":
 * numeric, non-zero (0,0 = never geocoded), and inside the service region.
 */
export function usableCoords(
  lat: number | null | undefined,
  lng: number | null | undefined,
): boolean {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    lat !== 0 &&
    lng !== 0 &&
    inServiceArea(lat, lng)
  );
}

const EARTH_MILES = 3958.8;
// Roads aren't straight lines — scale great-circle distance toward driving.
const ROAD_FACTOR = 1.3;

function toRad(d: number): number {
  return (d * Math.PI) / 180;
}

export function haversineMiles(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_MILES * Math.asin(Math.min(1, Math.sqrt(h))) * ROAD_FACTOR;
}

/** Total length (miles) of an ordered open route. */
export function routeMiles<T extends LatLng>(seq: T[]): number {
  let m = 0;
  for (let i = 1; i < seq.length; i++) m += haversineMiles(seq[i - 1], seq[i]);
  return m;
}

/** Greedy nearest-neighbor ordering, optionally seeded from a start point. */
function nearestNeighborFrom<T extends LatLng>(stops: T[], start?: LatLng): T[] {
  if (stops.length <= 1) return stops.slice();
  const remaining = stops.slice();
  const route: T[] = [];
  let cursor: LatLng;
  if (start) {
    cursor = start;
  } else {
    cursor = remaining[0];
    route.push(remaining.shift() as T);
  }
  while (remaining.length) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = haversineMiles(cursor, remaining[i]);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    route.push(next);
    cursor = next;
  }
  return route;
}

/** 2-opt improvement on an open route. */
function twoOpt<T extends LatLng>(route: T[]): T[] {
  const r = route.slice();
  let improved = true;
  while (improved) {
    improved = false;
    for (let i = 0; i < r.length - 1; i++) {
      for (let k = i + 1; k < r.length; k++) {
        const before =
          (i > 0 ? haversineMiles(r[i - 1], r[i]) : 0) +
          (k < r.length - 1 ? haversineMiles(r[k], r[k + 1]) : 0);
        const after =
          (i > 0 ? haversineMiles(r[i - 1], r[k]) : 0) +
          (k < r.length - 1 ? haversineMiles(r[i], r[k + 1]) : 0);
        if (after + 1e-9 < before) {
          let lo = i;
          let hi = k;
          while (lo < hi) {
            const tmp = r[lo];
            r[lo] = r[hi];
            r[hi] = tmp;
            lo++;
            hi--;
          }
          improved = true;
        }
      }
    }
  }
  return r;
}

/**
 * Pure geographic optimize (no constraints) — nearest-neighbor + 2-opt. Keeps a
 * reasonable start. Used by the SEG-01 batcher where there's no time order.
 */
export function optimizeRoute<T extends LatLng>(stops: T[]): T[] {
  if (stops.length <= 2) return stops.slice();
  return twoOpt(nearestNeighborFrom(stops));
}

/**
 * Window-aware optimize (ROUTE-04a). Each stop carries a `band` (a time rank —
 * e.g. promised hour, with after-hours forced last). We keep bands in ascending
 * order (so a 5 PM after-hours call can never jump ahead of a morning call) and
 * optimize geography only WITHIN a band, chaining each band from where the
 * previous one ended. `start` anchors the first band at the tech's current
 * position (the active stop), so a mid-day re-optimize plans from where he is.
 */
export function optimizeBanded<T extends LatLng & { band: number }>(
  stops: T[],
  start?: LatLng,
): T[] {
  if (stops.length <= 1) return stops.slice();
  const bands = [...new Set(stops.map((s) => s.band))].sort((a, b) => a - b);
  const result: T[] = [];
  let cursor: LatLng | undefined = start;
  for (const b of bands) {
    const group = stops.filter((s) => s.band === b);
    const ordered = twoOpt(nearestNeighborFrom(group, cursor));
    result.push(...ordered);
    if (ordered.length) cursor = ordered[ordered.length - 1];
  }
  return result;
}

/** Rough drive time (minutes) for a mileage at an average metro speed. */
export function driveMinutes(miles: number, mph = 28): number {
  return (miles / mph) * 60;
}

/**
 * Marginal miles to add a point to an existing open route (best insertion) —
 * used by the cross-tech move to show what each tech "gains" by taking a call.
 */
export function insertionCostMiles<T extends LatLng>(
  route: T[],
  p: LatLng,
): number {
  if (route.length === 0) return 0;
  if (route.length === 1) return haversineMiles(route[0], p);
  let best = Math.min(
    haversineMiles(p, route[0]), // prepend
    haversineMiles(route[route.length - 1], p), // append
  );
  for (let i = 0; i < route.length - 1; i++) {
    const add =
      haversineMiles(route[i], p) +
      haversineMiles(p, route[i + 1]) -
      haversineMiles(route[i], route[i + 1]);
    if (add < best) best = add;
  }
  return best;
}
